import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { SkeletonModule } from 'primeng/skeleton';
import { trigger, style, transition, animate } from '@angular/animations';
import { UserStateService } from '@core/services/user-state/user-state.service';
import { NotificationService } from '@shared/services/notifications/notification.service';
import { finalize, first } from 'rxjs/operators';
import { AuthFirebaseService } from '@core/services/auth-firebase/auth-firebase.service';
import { TwoFactorAuthenticationService } from '@core/auth/two-factor-authentication/service/two-factor-authentication.service';
import { SideMenuGroupService } from '@shared/services/side-menu-group/side-menu-group.service';
import { SideMenuItemDto } from '@core/dtos/side-menu-items.dto';
import { LoadingStateHandlerService } from '@shared/services/loading-state-handler/loading-state-handler.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { createNumberArray } from '@shared/utils/create-number-array';
import { TooltipIfTruncatedDirective } from '@shared/directives/tooltip-if-truncated.directive';
import { APP_VERSION } from '@core/constants/app-version.constant';

@Component({
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    RouterModule,
    TooltipModule,
    MenuModule,
    SkeletonModule,
    TranslateModule,
    TooltipIfTruncatedDirective,
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  animations: [
    trigger('submenuAnimation', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        animate('200ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1 }),
        animate('200ms ease-in', style({ height: 0, opacity: 0 })),
      ]),
    ]),
  ],
})
export class SidebarComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isExpanded: boolean = true;
  @Input() isDarkTheme: boolean = false;
  @Output() toggleSidebarEvent: EventEmitter<void> = new EventEmitter<void>();
  loadingComponent: boolean = true;
  loading: boolean = false;
  menuItems: SideMenuItemDto[] = [];

  readonly MENU_ITEM_ID_COMPANIES: number = 5;
  readonly PROFILE_ID_ADMINISTRATOR: number = 7;
  readonly appVersion: string = APP_VERSION;
  protected readonly createNumberArray: (length: number) => number[] =
    createNumberArray;

  accountOptions: any[] = [];
  private langChangeSubscription: Subscription;

  constructor(
    private authFirebaseService: AuthFirebaseService,
    public userStateService: UserStateService,
    private router: Router,
    private notificationService: NotificationService,
    private twoFactorAuthenticationService: TwoFactorAuthenticationService,
    private sideMenuGroupService: SideMenuGroupService,
    private loadingStateHandlerService: LoadingStateHandlerService,
    private translateService: TranslateService
  ) {
    this.setLoadingHandler();
    
    // Suscribirse a los cambios de idioma
    this.langChangeSubscription = this.translateService.onLangChange.subscribe(
      (_event: LangChangeEvent) => {
        this.initializeAccountOptions();
      }
    );
  }

  ngOnInit(): void {
    this.initializeAccountOptions();
    this.loadSideMenuItems();
  }

  private initializeAccountOptions(): void {
    this.accountOptions = [
      {
        label: this.translateService.instant('layout.sidebar.configureTwoFactor'),
        icon: 'pi pi-shield',
        styleClass: 'text-sm',
        command: () => this.openTwoFactorConfigurationDialog(),
      },
    ];
  }

  private setLoadingHandler() {
    this.loadingStateHandlerService.loadDataState(this.constructor.name, {
      sideMenuItems: false,
    });

    this.loadingStateHandlerService
      .getLoadingState(this.constructor.name)
      .pipe(takeUntilDestroyed())
      .subscribe((loading) => (this.loadingComponent = loading));
  }

  toggleSidebar() {
    this.toggleSidebarEvent.emit();
  }

  toggleSubMenu(item: any) {
    if (!item.hasSubMenu) return;

    if (!this.isExpanded) {
      this.isExpanded = true;
      this.toggleSidebar();
    }

    this.menuItems.forEach((menuItem) => {
      if (menuItem.hasSubMenu && menuItem !== item) menuItem.expanded = false;
    });

    item.expanded = !item.expanded;
  }

  openTwoFactorConfigurationDialog() {
    this.twoFactorAuthenticationService.openMfaDialog();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isExpanded'] || changes['isExpanded'].currentValue !== false)
      return;

    this.menuItems.forEach((item) => {
      if (item.hasSubMenu) item.expanded = false;
    });
  }

  loadSideMenuItems() {
    this.loadingComponent = true;
    const menuItems: SideMenuItemDto[] | null =
      this.userStateService.sideMenuItems();
    if (menuItems && menuItems.length > 0) {
      this.menuItems = menuItems;
      this.loadingStateHandlerService.setDataLoaded(
        this.constructor.name,
        'sideMenuItems'
      );
      return;
    }
    this.sideMenuGroupService
      .getSideMenuItems()
      .pipe(
        first(),
        finalize(() => {
          this.loadingStateHandlerService.setDataLoaded(
            this.constructor.name,
            'sideMenuItems'
          );
        })
      )
      .subscribe({
        next: (resp) => {
          const currentProfileId: number | null = this.userStateService.currentProfile()?.id ?? null;
          if (currentProfileId === this.PROFILE_ID_ADMINISTRATOR) {
            this.menuItems = resp.data;
            return;
          }

          this.menuItems = resp.data.filter(
            (item) => item.id !== this.MENU_ITEM_ID_COMPANIES
          );

        },
        error: (error) => {
          this.menuItems = [];
          this.notificationService.show(
            'Error',
            this.translateService.instant('layout.sidebar.errorLoadingMenuItems'),
            'error',
            error
          );
        },
      });
  }

  logout(): void {
    this.loading = true;

    this.authFirebaseService
      .logout()
      .pipe(
        first(),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: () => {
          this.userStateService.clearUserInfo();
          this.router.navigate(['/login']);
        },
        error: () => {
          this.notificationService.show('Error', this.translateService.instant('layout.sidebar.errorClosingSession'));
        },
      });
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }
}
